import {
  Box,
  Link,
  List,
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { Seo } from '../components/seo/Seo';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { organization, breadcrumbList } from '../utils/seo/jsonLd';

/**
 * "Round Recon vs. AmmoSeek" comparison page (#229). A public, indexable
 * page that compares Round Recon with AmmoSeek honestly and without
 * badmouthing the competitor. Claims about AmmoSeek are kept factual and
 * neutral and hedged with "to our knowledge"; the page frames the
 * comparison around Round Recon's genuine strengths (faceted filters,
 * price-per-round sorting, data-freshness labels, alerts, privacy, and
 * accessibility). SEO is a build requirement (§16.4) — this page carries
 * its own title/description/canonical and Organization + BreadcrumbList
 * JSON-LD.
 */

interface ComparisonRow {
  feature: string;
  roundRecon: string;
  ammoSeek: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: 'How listings are sourced',
    roundRecon:
      'Indexes retailer websites directly. Retailers do not pay or sign up to appear — coverage is editorial, not pay-to-play.',
    ammoSeek:
      'To our knowledge, retailers supply data through a proprietary feed format they adhere to, which can mean more curated feeds but a sign-up/relationship to be listed.',
  },
  {
    feature: 'Cost for retailers to appear',
    roundRecon: 'Free. Any retailer we can index can show up in results.',
    ammoSeek:
      'To our knowledge, retailers participate via AmmoSeek’s program rather than being indexed automatically.',
  },
  {
    feature: 'Price-per-round comparison',
    roundRecon:
      'Price per round is computed for every listing and is a first-class sort, so the real cost is never buried behind case count.',
    ammoSeek: 'Also surfaces cost per round — a long-standing, useful feature of the category.',
  },
  {
    feature: 'Filtering',
    roundRecon:
      'Strong faceted filters: caliber, brand, bullet type, case material, grain weight, round count, and use case, with the filter state captured in the URL so searches are shareable.',
    ammoSeek: 'Offers caliber, grain, and related filters common to the category.',
  },
  {
    feature: 'Data freshness',
    roundRecon:
      'Every listing shows a freshness state (fresh / aging / stale) and when it was last confirmed, so you can judge how current a price is.',
    ammoSeek:
      'To our knowledge, listings are refreshed regularly; an explicit per-listing freshness label is something Round Recon makes prominent.',
  },
  {
    feature: 'In-stock alerts',
    roundRecon:
      'Free accounts can save searches and get notified when a cartridge comes into stock or drops under a target price, with deduplication and quiet hours.',
    ammoSeek: 'Offers notification features for members.',
  },
  {
    feature: 'Account required to search',
    roundRecon: 'No. You can search, filter, and compare without creating an account.',
    ammoSeek: 'To our knowledge, core search is also usable without an account.',
  },
  {
    feature: 'Privacy',
    roundRecon:
      'Consent-gated analytics and personalization; opt-outs are enforced across the product, not just hidden in the UI.',
    ammoSeek: 'See AmmoSeek’s own privacy policy for their current practices.',
  },
  {
    feature: 'Accessibility',
    roundRecon:
      'Built to WCAG 2.2 AA as a requirement, with keyboard support and screen-reader-friendly markup throughout.',
    ammoSeek: 'We have not formally evaluated AmmoSeek’s accessibility and make no claim about it.',
  },
  {
    feature: 'Development',
    roundRecon: 'New and actively developed with a modern, evolving feature set.',
    ammoSeek:
      'An established, well-known player in the space; we make no claim about its current development pace.',
  },
];

export function VsAmmoSeekPage() {
  return (
    <>
      <Seo
        title="Round Recon vs. AmmoSeek — ammunition price comparison"
        description="An honest, side-by-side comparison of Round Recon and AmmoSeek for finding in-stock ammunition: how listings are sourced, filters, price-per-round, freshness, alerts, privacy, and accessibility."
        canonical={buildCanonicalUrl('/roundrecon-vs-ammoseek')}
        jsonLd={[
          organization(),
          breadcrumbList([
            { name: 'Home', url: '/' },
            { name: 'Round Recon vs. AmmoSeek', url: '/roundrecon-vs-ammoseek' },
          ]),
        ]}
      />
      <Stack spacing={3} sx={{ maxWidth: 'md' }}>
        <Typography variant="h1" component="h1">
          Round Recon vs. AmmoSeek
        </Typography>
        <Typography
          variant="body1"
          sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'text.secondary' }}
        >
          AmmoSeek is a well-known ammunition search site, and a lot of shooters have used it for
          years. Round Recon is a newer, independent take on the same problem: find in-stock
          ammunition fast and compare the real cost per round. This page lays out — as fairly as we
          can — where the two are similar and where Round Recon does things differently, so you can
          decide which fits how you shop.
        </Typography>

        <Box component="section" aria-labelledby="at-a-glance">
          <Typography
            id="at-a-glance"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            At a glance
          </Typography>
          <TableContainer>
            <Table size="small" aria-label="Feature comparison between Round Recon and AmmoSeek">
              <caption style={{ textAlign: 'left', captionSide: 'top' }}>
                How Round Recon and AmmoSeek compare across the things that matter when you’re
                buying ammunition. Statements about AmmoSeek are our understanding and may change —
                please verify on their site.
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    Feature
                  </TableCell>
                  <TableCell component="th" scope="col">
                    Round Recon
                  </TableCell>
                  <TableCell component="th" scope="col">
                    AmmoSeek
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {COMPARISON_ROWS.map((row) => (
                  <TableRow key={row.feature}>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>
                      {row.feature}
                    </TableCell>
                    <TableCell>{row.roundRecon}</TableCell>
                    <TableCell>{row.ammoSeek}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box component="section" aria-labelledby="sourcing">
          <Typography id="sourcing" variant="h2" component="h2" sx={{ fontSize: '1.4rem', mb: 1 }}>
            How listings are sourced
          </Typography>
          <Typography variant="body1">
            The biggest structural difference is where the listings come from. To our knowledge,
            AmmoSeek works largely from a proprietary feed format that participating retailers
            adhere to — that can produce clean, retailer-curated data, but it also means a retailer
            generally takes part in their program to be listed. Round Recon indexes retailer
            websites directly: we read publicly available product pages, normalize their
            inconsistent data into one comparable schema, and surface them. Retailers don&apos;t pay
            and don&apos;t sign up to appear, so coverage is driven by what we can index rather than
            by who participates.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="experience">
          <Typography
            id="experience"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            The everyday experience
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Round Recon is built around a clean, modern interface and the handful of things that
            actually speed up a purchase:
          </Typography>
          <List sx={{ pl: 2, listStyleType: 'disc' }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Sort by price per round.</strong> Compare apples to apples — sort by{' '}
              <Link component={RouterLink} to="/search?sort=pricePerRound&sortDir=asc">
                best price per round
              </Link>{' '}
              instead of guessing from the sticker price.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Filter hard.</strong> Narrow by caliber, bullet type, case material, grain
              weight, round count, and use case, then share the exact URL.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Trust the freshness label.</strong> Every listing shows how recently we
              confirmed it, so a stale price never masquerades as a live one.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Set in-stock alerts.</strong> A free{' '}
              <Link component={RouterLink} to="/register">
                account
              </Link>{' '}
              lets you save a search and get notified when a cartridge drops into stock or under
              your target price.
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <strong>Search without an account.</strong> Searching, filtering, and comparing never
              require signing up.
            </ListItem>
          </List>
        </Box>

        <Box component="section" aria-labelledby="privacy-a11y">
          <Typography
            id="privacy-a11y"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            Privacy and accessibility
          </Typography>
          <Typography variant="body1">
            Round Recon treats privacy and accessibility as requirements, not afterthoughts.
            Analytics and personalization are consent-gated, and opt-outs are enforced throughout
            the product rather than just hidden from view. The interface is built to{' '}
            <strong>WCAG 2.2 AA</strong>, with keyboard support and screen-reader-friendly markup so
            the whole site is usable however you navigate. We haven&apos;t formally evaluated
            AmmoSeek on these points, so we make no claim about them — we simply hold ourselves to
            this bar.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="disclaimer">
          <Typography
            id="disclaimer"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            A note on fairness
          </Typography>
          <Typography variant="body1">
            This is Round Recon&apos;s perspective, and we have an obvious interest in you choosing
            us. We&apos;ve tried to describe AmmoSeek accurately and neutrally — where we&apos;re
            characterizing how they work, we&apos;ve said &ldquo;to our knowledge,&rdquo; because
            their product can change at any time. AmmoSeek is a capable, long-running service, and
            the best way to judge either site is to try both with the calibers you actually buy. For
            the most accurate picture of AmmoSeek, see their own site and policies.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="get-started">
          <Typography
            id="get-started"
            variant="h2"
            component="h2"
            sx={{ fontSize: '1.4rem', mb: 1 }}
          >
            Try it yourself
          </Typography>
          <Typography variant="body1">
            The fastest way to compare is to run a search. Head to the{' '}
            <Link component={RouterLink} to="/search">
              listings
            </Link>{' '}
            and search for your caliber — no account required — or read more{' '}
            <Link component={RouterLink} to="/about">
              about Round Recon
            </Link>
            .
          </Typography>
        </Box>
      </Stack>
    </>
  );
}

export default VsAmmoSeekPage;
